import { useCallback } from "react";
import { TensileRequirementByGrade } from "../../../models/TensileRequirementByGrade";

type MinMaxField = "Yield" | "Tensile";

interface MinMaxParsedValue {
	min: number | null;
	max: number | null;
	asterisks: string;
}

export const useTableFunctions = () => {
	const extractAsterisks = useCallback(
		(input: string): { text: string; asterisks: string } => {
			const match = input.match(/(\*+)\s*$/);
			if (match) {
				return {
					text: input.slice(0, match.index).trim(),
					asterisks: match[1],
				};
			}
			return { text: input.trim(), asterisks: "" };
		},
		[],
	);

	const formatMinMaxDisplay = useCallback(
		(
			min: number | null | undefined,
			max: number | null | undefined,
			unit: string = "",
			asterisks: string = "",
		): string => {
			const hasMin = min != null;
			const hasMax = max != null;
			const suffix = asterisks ? ` ${asterisks}` : "";
			const unitPart = unit ? ` ${unit}` : "";

			if (hasMin && hasMax) {
				return `${min} - ${max}${unitPart}${suffix}`;
			}
			if (hasMin) {
				return `${min}${unitPart} Min${suffix}`;
			}
			if (hasMax) {
				return `${max}${unitPart} Max${suffix}`;
			}
			return asterisks || "";
		},
		[],
	);

	const getCombinedFieldDisplay = useCallback(
		(
			row: TensileRequirementByGrade,
			field: MinMaxField | "Elongation",
		): string => {
			if (field === "Elongation") {
				const percentMin = row.ElongationPercentMin;
				const length = row.ElongationLength;

				if (percentMin == null) {
					return "";
				}

				const lengthPart = length ? ` (${length})` : "";
				return `${percentMin}% Min${lengthPart}`;
			}

			const minField = `${field}Min` as keyof TensileRequirementByGrade;
			const maxField = `${field}Max` as keyof TensileRequirementByGrade;

			const min = row[minField] as number | null;
			const max = row[maxField] as number | null;
			const unit = row.Unit || "";

			// Extract asterisks from the stored display value
			const rawValue = row[field] as string | null;
			const { asterisks } = extractAsterisks(rawValue || "");

			return formatMinMaxDisplay(min, max, unit, asterisks);
		},
		[formatMinMaxDisplay, extractAsterisks],
	);

	const parseMinMaxInput = useCallback(
		(input: string): MinMaxParsedValue => {
			// Extract asterisks first
			const { text, asterisks } = extractAsterisks(input);
			const trimmed = text.toLowerCase();

			if (!trimmed) {
				return { min: null, max: null, asterisks };
			}

			// Check for range format: "60 - 95" or "60-95"
			const rangeMatch = trimmed.match(/^([\d.]+)\s*-\s*([\d.]+)/);
			if (rangeMatch) {
				const min = parseFloat(rangeMatch[1]);
				const max = parseFloat(rangeMatch[2]);
				return {
					min: isNaN(min) ? null : min,
					max: isNaN(max) ? null : max,
					asterisks,
				};
			}

			// Check for "Min" format: "60 Min" or "60 ksi Min" or "60 N/mm2 Min"
			const minMatch = trimmed.match(/^([\d.]+)\s*(?:\S+\s+)?min/i);
			if (minMatch) {
				const min = parseFloat(minMatch[1]);
				return {
					min: isNaN(min) ? null : min,
					max: null,
					asterisks,
				};
			}

			// Check for "Max" format: "36 Max" or "36 ksi Max" or "36 N/mm2 Max"
			const maxMatch = trimmed.match(/^([\d.]+)\s*(?:\S+\s+)?max/i);
			if (maxMatch) {
				const max = parseFloat(maxMatch[1]);
				return {
					min: null,
					max: isNaN(max) ? null : max,
					asterisks,
				};
			}

			// Just a number - default to min
			const numMatch = trimmed.match(/^([\d.]+)/);
			if (numMatch) {
				const num = parseFloat(numMatch[1]);
				return {
					min: isNaN(num) ? null : num,
					max: null,
					asterisks,
				};
			}

			return { min: null, max: null, asterisks };
		},
		[extractAsterisks],
	);

	const isCombinedMinMaxField = useCallback(
		(
			field: keyof TensileRequirementByGrade,
		): field is "Yield" | "Tensile" | "Elongation" => {
			return field === "Yield" || field === "Tensile" || field === "Elongation";
		},
		[],
	);

	const isElongationField = useCallback(
		(field: keyof TensileRequirementByGrade): boolean => {
			return field === "ElongationPercentMin" || field === "ElongationLength";
		},
		[],
	);

	const isMinField = useCallback(
		(field: keyof TensileRequirementByGrade): boolean => {
			return field === "YieldMin" || field === "TensileMin";
		},
		[],
	);

	const isMaxField = useCallback(
		(field: keyof TensileRequirementByGrade): boolean => {
			return field === "YieldMax" || field === "TensileMax";
		},
		[],
	);

	const getBaseFieldName = useCallback(
		(field: keyof TensileRequirementByGrade): MinMaxField | null => {
			if (field === "Yield" || field === "YieldMin" || field === "YieldMax")
				return "Yield";
			if (
				field === "Tensile" ||
				field === "TensileMin" ||
				field === "TensileMax"
			)
				return "Tensile";
			return null;
		},
		[],
	);

	const formatElongationDisplay = useCallback(
		(percentMin: number | null, length: string | null): string => {
			if (percentMin == null) {
				return "";
			}
			const lengthPart = length ? ` (${length})` : "";
			return `${percentMin}% Min${lengthPart}`;
		},
		[],
	);

	const processFieldUpdate = useCallback(
		(
			row: TensileRequirementByGrade,
			field: keyof TensileRequirementByGrade,
			value: any,
		): Partial<TensileRequirementByGrade> => {
			const updates: Partial<TensileRequirementByGrade> = {};
			const baseField = getBaseFieldName(field);
			const unit = row.Unit || "";

			if (field === "Elongation") {
				// Parse elongation input - expect format like "20 % Min (2in)" or just "20"
				const strValue = String(value).trim();
				const match = strValue.match(/^([\d.]+)/);
				const percentMin = match ? parseFloat(match[1]) : null;

				const lengthMatch = strValue.match(/\(([^)]+)\)/);
				const length = lengthMatch ? lengthMatch[1] : row.ElongationLength;

				updates.ElongationPercentMin = percentMin;
				updates.ElongationLength = length;
				updates.Elongation = formatElongationDisplay(percentMin, length);
				return updates;
			}

			if (field === "ElongationPercentMin") {
				const numValue = value === "" || value === null ? null : Number(value);
				const parsedMin =
					numValue === null || isNaN(numValue) ? null : numValue;
				updates.ElongationPercentMin = parsedMin;
				updates.Elongation = formatElongationDisplay(
					parsedMin,
					row.ElongationLength,
				);
				return updates;
			}

			if (field === "ElongationLength") {
				updates.ElongationLength = value;
				updates.Elongation = formatElongationDisplay(
					row.ElongationPercentMin,
					value,
				);
				return updates;
			}

			if (!baseField) {
				// Not a Min/Max related field, just return the single update
				updates[field] = value;
				return updates;
			}

			const minField = `${baseField}Min` as keyof TensileRequirementByGrade;
			const maxField = `${baseField}Max` as keyof TensileRequirementByGrade;

			if (isCombinedMinMaxField(field)) {
				// Editing combined field (Yield/Tensile)
				// Parse the input and update both Min and Max
				const parsed = parseMinMaxInput(String(value));

				updates[minField] = parsed.min as any;
				updates[maxField] = parsed.max as any;
				// Format display with asterisks preserved and row's unit
				updates[field] = formatMinMaxDisplay(
					parsed.min,
					parsed.max,
					unit,
					parsed.asterisks,
				) as any;
			} else if (isMinField(field)) {
				// Editing Min field - update Min and recalculate display
				const numValue = value === "" || value === null ? null : Number(value);
				const parsedMin =
					numValue === null || isNaN(numValue) ? null : numValue;
				const currentMax = row[maxField] as number | null;

				updates[minField] = parsedMin as any;
				updates[baseField] = formatMinMaxDisplay(
					parsedMin,
					currentMax,
					unit,
				) as any;
			} else if (isMaxField(field)) {
				// Editing Max field - update Max and recalculate display
				const numValue = value === "" || value === null ? null : Number(value);
				const parsedMax =
					numValue === null || isNaN(numValue) ? null : numValue;
				const currentMin = row[minField] as number | null;

				updates[maxField] = parsedMax as any;
				updates[baseField] = formatMinMaxDisplay(
					currentMin,
					parsedMax,
					unit,
				) as any;
			}

			return updates;
		},
		[
			getBaseFieldName,
			isCombinedMinMaxField,
			isMinField,
			isMaxField,
			parseMinMaxInput,
			formatMinMaxDisplay,
			formatElongationDisplay,
		],
	);

	const getEditValue = useCallback(
		(
			row: TensileRequirementByGrade,
			field: keyof TensileRequirementByGrade,
		): string => {
			if (isCombinedMinMaxField(field)) {
				return getCombinedFieldDisplay(row, field);
			}
			const value = row[field];
			return value == null ? "" : String(value);
		},
		[isCombinedMinMaxField, getCombinedFieldDisplay],
	);

	return {
		formatMinMaxDisplay,
		formatElongationDisplay,
		getCombinedFieldDisplay,
		parseMinMaxInput,
		isCombinedMinMaxField,
		isElongationField,
		isMinField,
		isMaxField,
		extractAsterisks,
		getBaseFieldName,
		processFieldUpdate,
		getEditValue,
	};
};
